import { chain, map } from 'fp-ts/lib/TaskEither';
import { pipe } from 'fp-ts/lib/function';
import {
  checklistCodec,
  groupedInspectionListCodec,
  groupedIssueListCodec,
  groupedQuestionListCodec,
  inspectionListCodec,
  issueListCodec,
  questionListCodec,
} from './codec';
import {
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createPostRequest,
  decode,
  fetchApi,
  fetchBlobApi,
  jsonHeaders,
  setBody,
  setQuery,
} from '../..';
import { GroupByDates, InspectionGroupBy, IssueGroupBy, QuestionGroupBy } from './types';
import {
  ChecklistExplainQuery,
  encodeChecklistExplainQuery, encodeGeneralInspectionQuery,
  encodeInspectionQuery,
  encodeIssueQuery,
  encodeQuestionQuery, GeneralInspectionQuery,
  InspectionQuery,
  IssueQuery,
  QuestionQuery,
} from './query';
import { ReportWidgetDataPayload } from '@/services/api/resources/analytics-builder/model';
import { widgetDataPayload } from '@/services/api/resources/analytics-builder/codec';

const baseUrl = '/v2/analytics';

// inspections
export const inspectionList = (query: InspectionQuery) => pipe(
  `${baseUrl}/inspections`,
  createAuthorizedGetRequest,
  map(setQuery(encodeInspectionQuery(query))),
  chain(fetchApi),
  chain(decode(inspectionListCodec)),
);

export const groupedInspectionList = (groupBy: InspectionGroupBy | GroupByDates, query: InspectionQuery) => pipe(
  `${baseUrl}/inspections/by${groupBy}`,
  createAuthorizedGetRequest,
  map(setQuery(encodeInspectionQuery(query))),
  chain(fetchApi),
  chain(decode(groupedInspectionListCodec)),
);

export const groupedGeneralInspectionList = (groupBy: InspectionGroupBy | GroupByDates, query: GeneralInspectionQuery) => pipe(
  `${baseUrl}/inspections/by${groupBy}`,
  createAuthorizedGetRequest,
  map(setQuery(encodeGeneralInspectionQuery(query))),
  chain(fetchApi),
  chain(decode(groupedInspectionListCodec)),
);

// issues
export const issueList = (query: IssueQuery) => pipe(
  `${baseUrl}/issues`,
  createAuthorizedGetRequest,
  map(setQuery(encodeIssueQuery(query))),
  chain(fetchApi),
  chain(decode(issueListCodec)),
);

export const groupedIssueList = (groupBy: IssueGroupBy | GroupByDates, query: IssueQuery) => pipe(
  `${baseUrl}/issues/by${groupBy}`,
  createAuthorizedGetRequest,
  map(setQuery(encodeIssueQuery(query))),
  chain(fetchApi),
  chain(decode(groupedIssueListCodec)),
);

// questions
export const questionList = (query: QuestionQuery) => pipe(
  `${baseUrl}/questions`,
  createAuthorizedGetRequest,
  map(setQuery(encodeQuestionQuery(query))),
  chain(fetchApi),
  chain(decode(questionListCodec)),
);

export const groupedQuestionList = (groupBy: QuestionGroupBy | GroupByDates, query: QuestionQuery) => pipe(
  `${baseUrl}/questions/by${groupBy}`,
  createAuthorizedGetRequest,
  map(setQuery(encodeQuestionQuery(query))),
  chain(fetchApi),
  chain(decode(groupedQuestionListCodec)),
);

//add codec later when refactored
export const checklistDetail = (id: number) => pipe(
  `${baseUrl}/checklists/${id}`,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(checklistCodec)),
);

export const checklistInspections = (id: number, query: ChecklistExplainQuery) => pipe(
  `${baseUrl}/checklists/${id}/inspections`,
  createAuthorizedGetRequest,
  map(setQuery(encodeChecklistExplainQuery(query))),
  chain(fetchApi),
);

export const analyticsPhotos = (id: number) => pipe(
  `${baseUrl}/photos/${id}`,
  createAuthorizedGetRequest,
  chain(fetchBlobApi),
);

export const fetchQuestion = (checklistId: string, questionUuid: string) => pipe(
  `${baseUrl}/checklists/${checklistId}/questions/${questionUuid}`,
  createAuthorizedGetRequest,
  chain(fetchApi),
);

export const all = (payload: ReportWidgetDataPayload) => pipe(
  `${baseUrl}/all`,
  createAuthorizedPostRequest,
  map(setBody(widgetDataPayload.encode(payload))),
  chain(fetchApi),
);

export const allPublic = (payload: ReportWidgetDataPayload, reportId: number) => pipe(
  `/v2/public/analytics/all/${reportId}`,
  jsonHeaders(createPostRequest),
  setBody(widgetDataPayload.encode(payload)),
  fetchApi,
);
